// scatterplot.jsx
import React, { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { generateCoastlineAnnotations } from '../utils/worldMapAnnotations';
import worldMapImageDark from '../static/images/world_map_outline.jpg';
import worldMapImageLight from '../static/images/world_map_white.jpg';
import createScatterplot from 'regl-scatterplot';
import { scaleLinear, scaleLog } from 'd3-scale';
import { useTheme } from '../theme/ThemeContext';

const containerStyle = {
    position: 'relative',
    width: '100%',
    height: '100vh',
};

const Scatterplot = ({
    points,
    ids,
    colors,
    sizes,
    colorDict,
    scatterPointsCategory,
    scatterColorCategory,
    setScatterplot,
    setHoveredSampleInfo,
    setSelectedSampleInfo,
    currentPointsSelection,
    updateHistoryStack,
    possibleColorDict,
    colorsData,
    setHoverVisible,
    setHoverPointData,
    isHistoryNavigationRef,
}) => {
    const containerRef = useRef(null);
    const canvasRef = useRef(null);
    const scatterplotRef = useRef(null);
    const previousPointsRef = useRef(null);
    const previousColorsRef = useRef(null);
    const previousSizesRef = useRef(null);
    const previousColorDictRef = useRef(null);
    const [dimensions, setDimensions] = useState({ width: window.innerWidth, height: window.innerHeight });
    const altKeyRef = useRef(false);
    const { theme } = useTheme();

    const scatterplotStyle = useMemo(() => ({
        backgroundColor: theme.colors.background,
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: -1,
        overflowX: 'hidden',
        overflowY: 'hidden',
    }), [theme.colors.background]);

    const handleResize = useCallback(() => {
        setDimensions({ width: window.innerWidth, height: window.innerHeight });
    }, []);

    // Optimized change detection using shallow comparison and lengths
    const hasDataChanged = useCallback((newPoints, newColors, newSizes, newColorDict) => {
        const hasPointsChanged = !previousPointsRef.current ||
            newPoints.length !== previousPointsRef.current.length ||
            newPoints.some((point, i) =>
                !previousPointsRef.current[i] ||
                point[0] !== previousPointsRef.current[i][0] ||
                point[1] !== previousPointsRef.current[i][1]
            );

        const hasColorsChanged = !previousColorsRef.current ||
            newColors.length !== previousColorsRef.current.length ||
            newColors.some((color, i) => color !== previousColorsRef.current[i]);

        const hasSizesChanged = !previousSizesRef.current ||
            newSizes.length !== previousSizesRef.current.length ||
            newSizes.some((size, i) => size !== previousSizesRef.current[i]);

        const hasColorDictChanged = !previousColorDictRef.current ||
            Object.keys(newColorDict).length !== Object.keys(previousColorDictRef.current).length ||
            Object.keys(newColorDict).some(key =>
                !previousColorDictRef.current[key] ||
                newColorDict[key].some((val, i) => val !== previousColorDictRef.current[key][i])
            );

        return { hasPointsChanged, hasColorsChanged, hasSizesChanged, hasColorDictChanged };
    }, []);

    // Memoize point data transformation
    const pointsAndValues = useMemo(() => {
        if (!points.length || !sizes.length || !colors.length) return [];

        return points.map((point, index) => [
            point[0],
            point[1],
            sizes[index],
            colors[index],
        ]);
    }, [points, sizes, colors]);

    // Memoize color palette
    const colorPalette = useMemo(() => {
        return Object.keys(colorDict).map(key => colorDict[key]);
    }, [colorDict]);

    const drawScatterplot = useCallback(async (newPoints, newColors, newSizes, newColorDict, scatterPointsCategory) => {
        if (!scatterplotRef.current || newPoints.length === 0) return;

        const { hasPointsChanged, hasColorsChanged, hasSizesChanged, hasColorDictChanged } =
            hasDataChanged(newPoints, newColors, newSizes, newColorDict);

        if (!hasPointsChanged && !hasColorsChanged && !hasColorDictChanged && !hasSizesChanged) {
            return; // No changes, skip update
        }

        try {
            // Update scatterplot configuration
            scatterplotRef.current.set({
                opacity: [0.8, 0.8],
                pointSize: [5, 20],
                // pointSize: [12, 20],
                pointSizeSelected: 18,
                pointOutlineWidth: 6,
                opacityInactiveScale: 0.7,
                pointColor: colorPalette,
                keyMap: { alt: null, shift: 'lasso', cmd: "merge" },
                lassoOnLongPress: true,
                sizeBy: 'valueA',
                opacityBy: 'valueA',
                colorBy: 'valueB'
            });

            // Draw points - use the memoized pointsAndValues
            await scatterplotRef.current.draw(pointsAndValues, {
                transition: hasPointsChanged,
                transitionDuration: hasPointsChanged ? 2000 : 0,
                transitionEasing: 'cubicInOut',
            });

            // Filter near origin points for geographic data
            if (scatterPointsCategory === "geo") {
                const RADIUS_THRESHOLD = 0.02;
                const validIndexes = pointsAndValues
                    .map((point, index) => {
                        const [x, y] = point;
                        const distanceFromOrigin = Math.sqrt(x * x + y * y);
                        return distanceFromOrigin > RADIUS_THRESHOLD ? index : null;
                    })
                    .filter(index => index !== null);

                if (validIndexes.length > 0) {
                    scatterplotRef.current.filter(validIndexes);
                }
            }

            // Handle background annotations
            if (scatterPointsCategory === "geo") {
                scatterplotRef.current.set({ backgroundImage: null });
                setTimeout(() => {
                    const worldMapAnnotations = generateCoastlineAnnotations(theme);
                    scatterplotRef.current.drawAnnotations(worldMapAnnotations);
                }, 500);
            } else {
                scatterplotRef.current.drawAnnotations([]);
            }

            // Update refs
            previousPointsRef.current = newPoints;
            previousColorsRef.current = newColors;
            previousSizesRef.current = newSizes;
            previousColorDictRef.current = newColorDict;

        } catch (error) {
            console.error("Error drawing scatterplot:", error);
        }
    }, [hasDataChanged, colorPalette, pointsAndValues, theme]);

    const currentSelectionRef = useRef(null);

    useEffect(() => {
        currentSelectionRef.current = currentPointsSelection;
    }, [currentPointsSelection]);

    // Initialize scatterplot only once
    useEffect(() => {
        const canvas = canvasRef.current;
        if (canvas && !scatterplotRef.current) {
            const scatterplot = createScatterplot({
                canvas,
                width: dimensions.width,
                height: dimensions.height,
                sizeBy: 'valueA',
                opacityBy: 'valueA',
                colorBy: 'valueB',
                xScale: scaleLinear().domain([-1, 1]),
                yScale: scaleLinear().domain([-1, 1]),
            });

            scatterplotRef.current = scatterplot;
            setScatterplot(scatterplot);
        }

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            if (scatterplotRef.current) {
                scatterplotRef.current.destroy();
                scatterplotRef.current = null;
            }
        };
    }, []); // Empty dependency array - only run once

    // Event handlers - memoized to prevent recreation
    const handleKeyDown = useCallback((e) => {
        altKeyRef.current = e.altKey;
    }, []);

    const handleKeyUp = useCallback((e) => {
        if (e.key === 'Alt') {
            altKeyRef.current = false;
        }
    }, []);

    const handleSelect = useCallback((obj) => {
        const previousSelection = currentSelectionRef.current;
        const newSelection = obj.points;

        if (altKeyRef.current && previousSelection) {
            const intersection = newSelection.filter(point =>
                previousSelection.includes(point)
            );
            currentSelectionRef.current = intersection;
            scatterplotRef.current.select(intersection);

            if (!isHistoryNavigationRef.current) {
                updateHistoryStack(previousSelection);
            }
            isHistoryNavigationRef.current = false;

            setSelectedSampleInfo(intersection.map((pointId) => ids[pointId]));
        } else {
            currentSelectionRef.current = newSelection;

            if (!isHistoryNavigationRef.current) {
                updateHistoryStack(previousSelection);
            }
            isHistoryNavigationRef.current = false;

            setSelectedSampleInfo(newSelection.map((pointId) => ids[pointId]));
        }
    }, [updateHistoryStack, setSelectedSampleInfo, ids, isHistoryNavigationRef]);

    const handleHover = useCallback((pointIndex) => {
        if (pointIndex !== null && pointIndex !== undefined && ids[pointIndex] && colorsData) {
            const sampleId = ids[pointIndex];
            const sampleData = colorsData[pointIndex];

            if (sampleData) {
                setHoverPointData({
                    sampleId: sampleId,
                    biome: sampleData
                });
                setHoverVisible(true);
            }
        } else {
            // Always call these when not hovering over a valid point
            setHoverVisible(false);
            setHoverPointData(null);
        }
    }, [ids, colorsData, setHoverPointData, setHoverVisible]);

    const handleDeselect = useCallback(() => {
        setSelectedSampleInfo(null);
    }, [setSelectedSampleInfo]);

    // Subscribe to events - optimize dependencies
    useEffect(() => {
        if (!scatterplotRef.current?.subscribe) return;

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);

        scatterplotRef.current.subscribe('select', handleSelect);
        scatterplotRef.current.subscribe('deselect', handleDeselect);
        scatterplotRef.current.subscribe('pointover', handleHover);
        // Add pointout subscription to ensure hover disappears
        scatterplotRef.current.subscribe('pointout', () => {
            setHoverVisible(false);
            setHoverPointData(null);
        });

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
            if (scatterplotRef.current) {
                scatterplotRef.current.unsubscribe('select', handleSelect);
                scatterplotRef.current.unsubscribe('deselect', handleDeselect);
                scatterplotRef.current.unsubscribe('pointover', handleHover);
                scatterplotRef.current.unsubscribe('pointout');
            }
        };
    }, [handleKeyDown, handleKeyUp, handleSelect, handleDeselect, handleHover, setHoverVisible, setHoverPointData]);

    // Update dimensions
    useEffect(() => {
        if (scatterplotRef.current) {
            scatterplotRef.current.set({
                width: dimensions.width,
                height: dimensions.height,
            });
        }
    }, [dimensions]);

    // Main draw effect - optimized dependencies
    useEffect(() => {
        drawScatterplot(points, colors, sizes, colorDict, scatterPointsCategory);
    }, [drawScatterplot, points, colors, sizes, colorDict, scatterPointsCategory]);

    return <canvas ref={canvasRef} width={dimensions.width} height={dimensions.height} style={scatterplotStyle} />;
};

export default Scatterplot;